import type {
  BaseLogData,
  BaseRuleData,
  CreateRoleLinkData,
  ImpactPreviewPlan,
  UpdateQuotaData,
  UpdateRoleLinkData,
} from "../model/Model";
import type { StackFrame } from "stacktrace-js";

const fetchCache = new Map<string, Promise<Response>>();

// Default fetch options
const GetFetchOptions: RequestInit = {
  method: "GET",
  credentials: "include",
};

const PostFetchOptions: RequestInit = {
  method: "POST",
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};

const PutFetchOptions: RequestInit = {
  method: "PUT",
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};

const DeleteFetchOptions: RequestInit = {
  method: "DELETE",
  credentials: "include",
};

const cachedFetch = (
  url: string,
  options: RequestInit = GetFetchOptions
): Promise<Response> => {
  const method = options.method ?? "GET";
  const body = typeof options.body === "string" ? options.body : "";

  const cacheKey = `${method}@${url}@${body}`;

  if (fetchCache.has(cacheKey)) {
    return fetchCache.get(cacheKey)!;
  }

  const fetchPromise = fetch(url, options).finally(() => {
    fetchCache.delete(cacheKey);
  });

  fetchCache.set(cacheKey, fetchPromise);
  return fetchPromise;
};

// Base API path
const API = import.meta.env.VITE_API_PATH;

// Async API functions with explicit `return await`

export const userDetail = async () => {
  // await new Promise((resolve) => setTimeout(resolve, 5000)); // Delay 5 seconds
  return await cachedFetch(`${API}/discord/users/@me`, GetFetchOptions);
};

export const userManagerGuilds = async () => {
  return await cachedFetch(
    `${API}/discord/users/@me/manager-guilds`,
    GetFetchOptions
  );
};

export const userLogout = async () => {
  return await cachedFetch(`${API}/discord/auth/logout`, GetFetchOptions);
};

export const userBotGuilds = async () => {
  return await cachedFetch(`${API}/bot/users/guilds`, GetFetchOptions);
};

// --- Developer impersonation ("Login as user") ---
// whoami tells the banner whether the current session is impersonating (and who
// is driving / being impersonated), and tells developers they may start one.
export const impersonationWhoami = async () => {
  return await cachedFetch(`${API}/impersonation/whoami`, GetFetchOptions);
};

export const startImpersonation = async (
  targetUserId: string,
  reason: string
) => {
  return await fetch(`${API}/impersonation/start`, {
    ...PostFetchOptions,
    body: JSON.stringify({ targetUserId, reason }),
  });
};

export const stopImpersonation = async () => {
  return await fetch(`${API}/impersonation/stop`, PostFetchOptions);
};

export const quotaDetail = async () => {
  // await new Promise((resolve) => setTimeout(resolve, 5000)); // Delay 5 seconds
  return await cachedFetch(`${API}/quotas`, GetFetchOptions);
};

export const updateQuota = async (data: UpdateQuotaData) => {
  return await cachedFetch(`${API}/quotas`, {
    ...PostFetchOptions,
    body: JSON.stringify(data),
  });
};

export const resetQuota = async () => {
  return await cachedFetch(`${API}/quotas`, DeleteFetchOptions);
};

export const patreonLinkUrl = () => `${API}/patreon/link`;

export const patreonUnlink = async () => {
  return await cachedFetch(`${API}/patreon/unlink`, PostFetchOptions);
};

// Stripe (default payment rail). Both return `{ data: { url } }` for the
// caller to redirect to — Checkout for new subscriptions, Customer Portal for
// managing/cancelling an existing one.
/**
 * Starts an embedded Checkout session. `promotionCode` is the code the buyer
 * typed into our own field — Checkout renders no promotion-code box of its own,
 * because only a discount applied as the session is created can make Stripe skip
 * card entry (see the API's stripe-promo.ts). An unusable code comes back as a
 * 400 with a message to show beside the field; no session is opened.
 */
export const stripeCheckout = async (
  priceId: string,
  promotionCode?: string,
) => {
  const code = promotionCode?.trim();
  return await cachedFetch(`${API}/stripe/checkout`, {
    ...PostFetchOptions,
    body: JSON.stringify({ priceId, ...(code ? { promotionCode: code } : {}) }),
  });
};

export const stripePortal = async () => {
  return await cachedFetch(`${API}/stripe/portal`, PostFetchOptions);
};

export const userBotGuildsManager = async () => {
  // await new Promise((resolve) => setTimeout(resolve, 5000)); // Delay 5 seconds
  return await cachedFetch(
    `${API}/bot/users/guilds?manager=true`,
    GetFetchOptions
  );
};

export const guildRules = async (guildId: string) => {
  // await new Promise((resolve) => setTimeout(resolve, 2000)); // Delay 5 seconds
  return await cachedFetch(`${API}/rules/${guildId}`, GetFetchOptions);
};

/**
 * Bypass request coalescing for apply-time optimistic-concurrency checks.
 * A shared in-flight GET may have started before another admin's edit and is
 * therefore not a fresh snapshot even if it resolves immediately before use.
 */
export const freshGuildRules = async (guildId: string) => {
  return await fetch(`${API}/rules/${guildId}`, {
    ...GetFetchOptions,
    cache: "no-store",
  });
};

export const pauseGuildRule = async (guildId: string) => {
  return await cachedFetch(`${API}/rules/${guildId}/pause`, PostFetchOptions);
};

export const startGuildRule = async (guildId: string) => {
  return await cachedFetch(`${API}/rules/${guildId}/start`, PostFetchOptions);
};

export const createGuildRule = async (guildId: string, data: BaseRuleData) => {
  return await cachedFetch(`${API}/rules/${guildId}`, {
    ...PostFetchOptions,
    body: JSON.stringify(data),
  });
};

export const updateGuildRule = async (
  guildId: string,
  ruleId: number,
  data: BaseRuleData
) => {
  return await cachedFetch(`${API}/rules/${guildId}/${ruleId}`, {
    ...PutFetchOptions,
    body: JSON.stringify(data),
  });
};

export const guildRoles = async (guildId: string) => {
  // await new Promise((resolve) => setTimeout(resolve, 5000)); // Delay 5 seconds
  return await cachedFetch(
    `${API}/bot/guilds/${guildId}/roles`,
    GetFetchOptions
  );
};

export const deleteGuildRule = async (guildId: string, ruleId: number) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/${ruleId}`,
    DeleteFetchOptions
  );
};

/**
 * Delete several rules in one atomic request.
 *
 * Used when replacing a group of rules with a single equivalent one: deleting
 * them one at a time would leave the guild observably part-compacted between
 * requests and make the bot re-read the rule set once per deletion.
 */
export const batchDeleteGuildRules = async (
  guildId: string,
  ruleIds: number[]
) => {
  return await cachedFetch(`${API}/rules/${guildId}/batch-delete`, {
    ...PostFetchOptions,
    body: JSON.stringify({ rule_ids: ruleIds }),
  });
};

/**
 * Ask which rules the loop scan withholds from the bot before, during, and
 * after a proposed compaction. Writes nothing.
 *
 * The scan runs across every guild connected to this one, so a rule can be
 * enabled, unlocked and still not running — and the dashboard cannot work out
 * which, because the rule bodies that decide it belong to other servers. The
 * compaction engine needs the answer to know which rules its equivalence proof
 * is even about.
 */
// ---------------------------------------------------------------------------
// Safe Apply — estimate before apply, confirmations, deployments, undo.
// ---------------------------------------------------------------------------

/**
 * Dry-run a draft against the live member list. Returns the same estimate
 * and gate verdict a save would produce, without writing anything.
 */
export const guildRuleImpact = async (
  guildId: string,
  plan: ImpactPreviewPlan
) => {
  return await fetch(`${API}/rules/${guildId}/impact`, {
    ...PostFetchOptions,
    cache: "no-store",
    body: JSON.stringify(plan),
  });
};

/** Deployment, pending confirmations, held rules and undo state. Never coalesced: it is polled. */
export const getSafeApply = async (guildId: string) => {
  return await fetch(`${API}/rules/${guildId}/safe-apply`, {
    ...GetFetchOptions,
    cache: "no-store",
  });
};

/** Release the held rules of a version and deploy it (canary first when flagged). */
export const confirmVersion = async (guildId: string, versionId: number) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/versions/${versionId}/confirm`,
    PostFetchOptions
  );
};

/** Keep the rules saved but held; nothing is delivered to the bot. */
export const dismissVersion = async (guildId: string, versionId: number) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/versions/${versionId}/dismiss`,
    PostFetchOptions
  );
};

export const stopDeployment = async (guildId: string, deploymentId: number) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/deployments/${deploymentId}/stop`,
    PostFetchOptions
  );
};

/** Skip the rest of a canary hold and roll the deployment out now. */
export const continueDeployment = async (
  guildId: string,
  deploymentId: number
) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/deployments/${deploymentId}/continue`,
    PostFetchOptions
  );
};

/** Take a finished deployment off the status bar without undoing anything. */
export const dismissDeployment = async (
  guildId: string,
  deploymentId: number
) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/deployments/${deploymentId}/dismiss`,
    PostFetchOptions
  );
};

/** Put back every role a deployment changed that nobody has touched since. */
export const undoDeployment = async (guildId: string, deploymentId: number) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/deployments/${deploymentId}/undo`,
    PostFetchOptions
  );
};

/** Undo what one rule did over the last N hours (default 24). */
export const undoRule = async (
  guildId: string,
  ruleId: number,
  hours?: number
) => {
  return await cachedFetch(`${API}/rules/${guildId}/undo-rule/${ruleId}`, {
    ...PostFetchOptions,
    body: JSON.stringify(hours ? { hours } : {}),
  });
};

/** Lift a hold that was not an impact gate (hierarchy, role_deleted, budget). */
export const resumeRule = async (guildId: string, ruleId: number) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/${ruleId}/resume`,
    PostFetchOptions
  );
};

/**
 * Fresh numbers for a rule waiting on a confirmation that no pending version
 * names any more. The rule stays held; the result opens the confirm dialog.
 */
export const recheckRule = async (guildId: string, ruleId: number) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/${ruleId}/recheck`,
    PostFetchOptions
  );
};

/**
 * Drop the edit waiting on a rule for a confirmation. The rule keeps running
 * as it is; the confirmation that would have written the edit retires.
 */
export const discardPendingEdit = async (guildId: string, ruleId: number) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/${ruleId}/discard-edit`,
    PostFetchOptions
  );
};

export const getJournal = async (
  guildId: string,
  params?: {
    user_id?: string;
    rule_id?: number;
    deployment_id?: number;
    status?: string;
    page?: number;
    limit?: number;
  }
) => {
  const searchParams = new URLSearchParams();
  if (params?.user_id) searchParams.set("user_id", params.user_id);
  if (params?.rule_id) searchParams.set("rule_id", String(params.rule_id));
  if (params?.deployment_id)
    searchParams.set("deployment_id", String(params.deployment_id));
  if (params?.status) searchParams.set("status", params.status);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));
  const query = searchParams.toString();
  return await fetch(
    `${API}/rules/${guildId}/journal${query ? `?${query}` : ""}`,
    { ...GetFetchOptions, cache: "no-store" }
  );
};

export const guildRuleDeliveryCheck = async (
  guildId: string,
  plan: {
    update?: { rule_id: number; rule: BaseRuleData } | null;
    delete_rule_ids?: number[];
  }
) => {
  return await cachedFetch(`${API}/rules/${guildId}/delivery-check`, {
    ...PostFetchOptions,
    body: JSON.stringify(plan),
  });
};

export const guildLogs = async (guildId: string) => {
  return await cachedFetch(`${API}/logs/${guildId}`, GetFetchOptions);
};

export const createGuildLogs = async (guildId: string, data: BaseLogData) => {
  return await cachedFetch(`${API}/logs/${guildId}`, {
    ...PostFetchOptions,
    body: JSON.stringify(data),
  });
};

export const updateGuildLogs = async (
  guildId: string,
  logId: number,
  data: BaseLogData
) => {
  return await cachedFetch(`${API}/logs/${guildId}/${logId}`, {
    ...PutFetchOptions,
    body: JSON.stringify(data),
  });
};

export const deleteGuildLogs = async (guildId: string, logId: number) => {
  return await cachedFetch(
    `${API}/logs/${guildId}/${logId}`,
    DeleteFetchOptions
  );
};

// System Logs (Audit Trail)
export const getSystemLogs = async (
  guildId: string,
  params?: {
    page?: number;
    limit?: number;
    entity_type?: string;
    action?: string;
  }
) => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set("page", params.page.toString());
  if (params?.limit) searchParams.set("limit", params.limit.toString());
  if (params?.entity_type) searchParams.set("entity_type", params.entity_type);
  if (params?.action) searchParams.set("action", params.action);

  const queryString = searchParams.toString();
  const url = `${API}/system-logs/${guildId}${
    queryString ? `?${queryString}` : ""
  }`;
  return await cachedFetch(url, GetFetchOptions);
};

export const getActivityLogChannel = async (guildId: string) => {
  return await cachedFetch(
    `${API}/system-logs/${guildId}/channel`,
    GetFetchOptions
  );
};

export const setActivityLogChannel = async (
  guildId: string,
  channelId: string | null
) => {
  return await cachedFetch(`${API}/system-logs/${guildId}/channel`, {
    ...PutFetchOptions,
    body: JSON.stringify({ channel_id: channelId }),
  });
};

export const guildChannels = async (guildId: string) => {
  return await cachedFetch(
    `${API}/bot/guilds/${guildId}/channels`,
    GetFetchOptions
  );
};

export const purgeActionRoles = async (guildId: string) => {
  return await cachedFetch(`${API}/rules/${guildId}/prune`, PostFetchOptions);
};

export const purgeTargetGuildActionRoles = async (
  guildId: string,
  targetGuildId: string
) => {
  return await cachedFetch(
    `${API}/rules/${guildId}/prune/${targetGuildId}`,
    PostFetchOptions
  );
};

// Role Links
export const guildRoleLinks = async (guildId: string) => {
  return await cachedFetch(`${API}/role-link/${guildId}`, GetFetchOptions);
};

/**
 * Who holds each role right now (`RoleHoldersData`, or `null` while the bot
 * cannot count). Read by the New Role Link dialog so a role that already has
 * members is called out before it is handed to an integration.
 */
export const roleLinkHolders = async (guildId: string) => {
  return await cachedFetch(
    `${API}/role-link/${guildId}/holders`,
    GetFetchOptions
  );
};

export const createRoleLink = async (
  guildId: string,
  data: CreateRoleLinkData
) => {
  return await cachedFetch(`${API}/role-link/${guildId}`, {
    ...PostFetchOptions,
    body: JSON.stringify(data),
  });
};

export const updateRoleLink = async (
  guildId: string,
  roleId: string,
  data: UpdateRoleLinkData
) => {
  return await cachedFetch(`${API}/role-link/${guildId}/${roleId}`, {
    ...PutFetchOptions,
    body: JSON.stringify(data),
  });
};

/**
 * Apply a list shrink the integration sent and RoleLogic held. Also opens a
 * one-hour window in which the integration may resend the same shrink.
 */
export const confirmRoleLinkRemovals = async (
  guildId: string,
  roleId: string
) => {
  return await cachedFetch(
    `${API}/role-link/${guildId}/${roleId}/confirm-removals`,
    PostFetchOptions
  );
};

export const deleteRoleLink = async (guildId: string, roleId: string) => {
  return await cachedFetch(
    `${API}/role-link/${guildId}/${roleId}`,
    DeleteFetchOptions
  );
};

// Plugin endpoints
export const getPluginSchema = async (guildId: string, roleId: string) => {
  return await cachedFetch(
    `${API}/role-link/${guildId}/${roleId}/plugin/config`,
    GetFetchOptions
  );
};

export const submitPluginConfig = async (
  guildId: string,
  roleId: string,
  config: Record<string, unknown>
) => {
  return await cachedFetch(
    `${API}/role-link/${guildId}/${roleId}/plugin/config`,
    {
      ...PostFetchOptions,
      body: JSON.stringify(config),
    }
  );
};

export const refreshPluginSchema = async (
  guildId: string,
  roleId: string
) => {
  return await cachedFetch(
    `${API}/role-link/${guildId}/${roleId}/plugin/config/refresh`,
    PostFetchOptions
  );
};

/**
 * Sign a reimage image-proxy URL so the browser can fetch it from the
 * private (REQUIRE_AUTH=true) reimage server. Server-side: see
 * api/src/api/image-proxy/image-proxy.controller.ts. Returns the raw
 * Response; callers parse `{ url: string }` from JSON. Auth is the same
 * session cookie used by every other endpoint.
 */
export const signImageProxyUrl = async (url: string) => {
  return await cachedFetch(`${API}/image-proxy/sign`, {
    ...PostFetchOptions,
    body: JSON.stringify({ url }),
  });
};

// --- AI connections (MCP) ---
//
// RoleLogic runs no AI inference. These endpoints manage the user's grant to
// their *own* assistant — which servers it may see, what it may do, and what it
// has done. Every one of them is cookie-authenticated on purpose: the consent
// decision and the approval of a change must happen in the user's own session,
// never on a token an AI client holds.

/**
 * Describe a pending authorize request so the consent screen can render who is
 * asking. Takes the raw query string the OAuth redirect arrived with — the
 * server re-validates every field of it against the stored client registration,
 * so nothing here is trusted for being in the URL.
 */
export const mcpAuthorizeRequest = async (query: string) => {
  return await fetch(`${API}/mcp/oauth/authorize-request?${query}`, {
    ...GetFetchOptions,
    cache: "no-store",
  });
};

/** Record consent. Returns the URL to send the browser back to. */
export const mcpConsent = async (body: unknown) => {
  return await fetch(`${API}/mcp/oauth/consent`, {
    ...PostFetchOptions,
    body: JSON.stringify(body),
  });
};

/** Decline. Still returns a redirect, so the client shows "cancelled" rather
 *  than waiting for a callback that never comes. */
export const mcpDeny = async (body: unknown) => {
  return await fetch(`${API}/mcp/oauth/deny`, {
    ...PostFetchOptions,
    body: JSON.stringify(body),
  });
};

export const mcpConnections = async () => {
  return await fetch(`${API}/mcp/connections`, {
    ...GetFetchOptions,
    cache: "no-store",
  });
};

export const mcpRevokeConnection = async (id: string, reason?: string) => {
  return await fetch(`${API}/mcp/connections/${id}`, {
    ...DeleteFetchOptions,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(reason ? { reason } : {}),
  });
};

/**
 * Share more servers with a connection that already exists.
 *
 * Sends the servers to *add*, not the set to end up with, so this can never
 * un-share anything by being sent from a stale tab. Permissions, access level
 * and expiry are untouched — those stay replace-only, through a fresh consent
 * screen reached from the AI platform.
 */
export const mcpAddConnectionServers = async (
  id: string,
  guildIds: string[]
) => {
  return await fetch(`${API}/mcp/connections/${id}/servers`, {
    ...PostFetchOptions,
    body: JSON.stringify({ guild_ids: guildIds }),
  });
};

export const mcpActivity = async (params?: {
  page?: number;
  limit?: number;
  connection_id?: string;
  guild_id?: string;
  outcome?: string;
}) => {
  const search = new URLSearchParams();
  if (params?.page) search.set("page", String(params.page));
  if (params?.limit) search.set("limit", String(params.limit));
  if (params?.connection_id) search.set("connection_id", params.connection_id);
  if (params?.guild_id) search.set("guild_id", params.guild_id);
  if (params?.outcome) search.set("outcome", params.outcome);
  const query = search.toString();
  return await fetch(
    `${API}/mcp/connections/activity${query ? `?${query}` : ""}`,
    { ...GetFetchOptions, cache: "no-store" }
  );
};

/**
 * Counts only, cheap enough to poll from the account menu.
 *
 * Deliberately not `cachedFetch`: a badge that says "no approvals waiting"
 * from a stale cache is worse than no badge, because the user stops checking.
 */
export const mcpSummary = async () => {
  return await fetch(`${API}/mcp/connections/summary`, {
    ...GetFetchOptions,
    cache: "no-store",
  });
};

export const mcpChangeRequests = async (status = "pending") => {
  return await fetch(
    `${API}/mcp/connections/changes?status=${encodeURIComponent(status)}`,
    { ...GetFetchOptions, cache: "no-store" }
  );
};

/**
 * Approve or reject one queued change.
 *
 * Not request-coalesced: two clicks are two decisions, and collapsing them into
 * one shared promise would make a rejected change look approved.
 */
export const mcpDecideChange = async (
  id: string,
  decision: "approve" | "reject"
) => {
  return await fetch(`${API}/mcp/connections/changes/${id}`, {
    ...PostFetchOptions,
    body: JSON.stringify({ decision }),
  });
};

export const fetchPluginList = async () => {
  const base = import.meta.env.VITE_PLUGIN_LIST_URL as string;
  const url = base.replace(/\/list\.json(?=$|\?|#)/, "/list.compact.json");
  return await cachedFetch(url, { method: "GET" });
};

/**
 * Formats a parsed stack frame array into a readable string
 */
const formatStackFrames = (frames: StackFrame[]): string => {
  return frames
    .map((frame) => {
      const functionName = frame.functionName || "<anonymous>";
      const fileName = frame.fileName || "<unknown>";
      const lineNumber = frame.lineNumber ?? "?";
      const columnNumber = frame.columnNumber ?? "?";
      return `    at ${functionName} (${fileName}:${lineNumber}:${columnNumber})`;
    })
    .join("\n");
};

/**
 * Attempts to parse and map a minified stack trace using source maps.
 * Falls back to the original stack if parsing fails.
 *
 * stacktrace-js and the source-map parser under it are fetched here, on the
 * first error, not with the app: every page loads this module (it reports
 * errors from main.tsx), and a stack mapper nobody needs until something
 * breaks does not belong on the homepage's critical path. If that fetch
 * fails too — a tab left open across a deploy — the raw stack is sent.
 */
const getMappedStackTrace = async (
  error: Error
): Promise<string | undefined> => {
  try {
    const { default: StackTrace } = await import("stacktrace-js");
    const frames = await StackTrace.fromError(error, {
      offline: false, // Fetch source maps from the server
    });
    if (frames.length > 0) {
      return formatStackFrames(frames);
    }
  } catch (parseError) {
    console.warn("Failed to parse stack trace with source maps:", parseError);
  }
  // Fall back to original stack
  return error.stack;
};

export const logError = async (error: {
  message: string;
  stack?: string;
  mappedStack?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info?: any;
  // Original error object for stack trace parsing
  originalError?: Error;
}) => {
  let mappedStack = error.stack;

  // If we have the original Error object, try to map the stack trace
  if (error.originalError instanceof Error) {
    try {
      mappedStack = await getMappedStackTrace(error.originalError);
    } catch (e) {
      console.warn("Failed to get mapped stack trace:", e);
    }
  }

  const payload = {
    message: error.message,
    stack: error.stack, // Original minified stack (for reference)
    mappedStack, // Source-mapped stack (for debugging)
    info: error.info,
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  };

  return await cachedFetch(`${API}/log-error`, {
    ...PostFetchOptions,
    body: JSON.stringify(payload),
  });
};
